import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

// Simple Select component compatible with Shadcn API

interface SelectContextValue {
  value: string;
  displayValue: string;
  onValueChange: (value: string, displayValue?: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRect: DOMRect | null;
  registerItem: (value: string, label: string) => void;
  itemLabels: Map<string, string>;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

export function Select({ value, onValueChange, defaultValue, disabled, children }: SelectProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue || '');
  const [displayValue, setDisplayValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [triggerRect, setTriggerRect] = React.useState<DOMRect | null>(null);
  const [itemLabels] = React.useState(() => new Map<string, string>());
  
  const currentValue = value !== undefined ? value : internalValue;
  
  const registerItem = React.useCallback((itemValue: string, label: string) => {
    itemLabels.set(itemValue, label);
    // Update display value if this is the current value
    if (itemValue === currentValue && !displayValue) {
      setDisplayValue(label);
    }
  }, [itemLabels, currentValue, displayValue]);

  // Update display value when value changes
  React.useEffect(() => {
    if (currentValue && itemLabels.has(currentValue)) {
      setDisplayValue(itemLabels.get(currentValue) || currentValue);
    } else if (!currentValue) {
      setDisplayValue('');
    }
  }, [currentValue, itemLabels]);
  
  const handleValueChange = React.useCallback((newValue: string, newDisplayValue?: string) => {
    console.info('[Select] handleValueChange called with:', newValue, newDisplayValue);
    if (value === undefined) {
      setInternalValue(newValue);
    }
    setDisplayValue(newDisplayValue || itemLabels.get(newValue) || newValue);
    onValueChange?.(newValue);
    setOpen(false);
  }, [value, onValueChange, itemLabels]);

  const handleSetOpen = React.useCallback((newOpen: boolean) => {
    setOpen(newOpen);
  }, []);

  const contextValue = React.useMemo(() => ({
    value: currentValue,
    displayValue,
    onValueChange: handleValueChange,
    open,
    setOpen: handleSetOpen,
    triggerRect,
    registerItem,
    itemLabels,
  }), [currentValue, displayValue, handleValueChange, open, handleSetOpen, triggerRect, registerItem, itemLabels]);

  return (
    <SelectContext.Provider value={contextValue}>
      <SelectTriggerWrapper onRectChange={setTriggerRect}>
        {children}
      </SelectTriggerWrapper>
    </SelectContext.Provider>
  );
}

function SelectTriggerWrapper({ children, onRectChange }: { children: React.ReactNode; onRectChange: (rect: DOMRect | null) => void }) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const context = React.useContext(SelectContext);

  React.useEffect(() => {
    if (context?.open && wrapperRef.current) {
      const trigger = wrapperRef.current.querySelector('button');
      if (trigger) {
        onRectChange(trigger.getBoundingClientRect());
      }
    }
  }, [context?.open, onRectChange]);

  return (
    <div ref={wrapperRef} className="relative">
      {children}
    </div>
  );
}

interface SelectTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

export function SelectTrigger({ className, children, ...props }: SelectTriggerProps) {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectTrigger must be used within Select');

  return (
    <button
      type="button"
      data-select-trigger="true"
      className={cn(
        'flex h-10 w-full items-center justify-between rounded border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      onClick={() => context.setOpen(!context.open)}
      {...props}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform flex-shrink-0", context.open && "rotate-180")} />
    </button>
  );
}

interface SelectValueProps {
  placeholder?: string;
}

export function SelectValue({ placeholder }: SelectValueProps) {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectValue must be used within Select');

  const display = context.displayValue || context.value;

  return (
    <span className={cn("truncate", display ? '' : 'text-muted-foreground')}>
      {display || placeholder}
    </span>
  );
}

interface SelectContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  /** Render dropdown inline instead of portaling to document.body (useful inside dialogs) */
  portalled?: boolean;
}

export function SelectContent({ className, children, portalled = true, ...props }: SelectContentProps) {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectContent must be used within Select');

  const contentRef = React.useRef<HTMLDivElement>(null);

  // Close on click outside
  React.useEffect(() => {
    if (!context.open) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (contentRef.current && !contentRef.current.contains(target)) {
        // Check if click was on the trigger button
        const trigger = document.querySelector('[data-select-trigger="true"]');
        if (trigger && trigger.contains(target)) return;
        context.setOpen(false);
      }
    };

    // Delay to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [context.open, context]);

  // Close on escape
  React.useEffect(() => {
    if (!context.open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        context.setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [context.open, context]);

  if (!context.open) return null;
  if (portalled && !context.triggerRect) return null;

  const style: React.CSSProperties = portalled
    ? {
        position: 'fixed',
        top: (context.triggerRect as DOMRect).bottom + 4,
        left: (context.triggerRect as DOMRect).left,
        minWidth: (context.triggerRect as DOMRect).width,
        zIndex: 9999,
      }
    : {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        minWidth: '100%',
        zIndex: 50,
      };

  const content = (
    <div
      ref={contentRef}
      style={style}
      data-select-content="true"
      className={cn(
        'max-h-60 overflow-auto rounded border border-border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95',
        // Explicit background fallback for mobile/dialog contexts
        'backdrop-blur-none bg-background',
        className
      )}
      // Prevent click events from propagating to dialog overlay (fixes mobile selection issue)
      onPointerDownCapture={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      {children}
    </div>
  );

  return portalled ? createPortal(content, document.body) : content;
}

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  children?: React.ReactNode;
  /** Optional text label for display in trigger when selected */
  textValue?: string;
}

export function SelectItem({ value, className, children, textValue, ...props }: SelectItemProps) {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectItem must be used within Select');

  const isSelected = context.value === value;

  // Extract text content for display - prefer textValue prop, else extract from children
  const extractedLabel = React.useMemo(() => {
    if (textValue) return textValue;
    
    // Try to extract text from children
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      if (React.isValidElement(node)) {
        const props = node.props as { children?: React.ReactNode };
        const nodeChildren = props.children;
        if (typeof nodeChildren === 'string') return nodeChildren;
        if (Array.isArray(nodeChildren)) {
          // Return first text child (usually the label)
          for (const child of nodeChildren) {
            const text = extractText(child);
            if (text) return text;
          }
        }
        return extractText(nodeChildren);
      }
      return '';
    };
    
    return extractText(children) || value;
  }, [textValue, children, value]);

  // Register this item's label
  React.useEffect(() => {
    context.registerItem(value, extractedLabel);
  }, [context, value, extractedLabel]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    context.onValueChange(value, extractedLabel);
  };

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
        isSelected && 'bg-accent/50',
        className
      )}
      onClick={handleClick}
      onPointerDownCapture={(e) => e.stopPropagation()}
      {...props}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4" />
        </span>
      )}
      {children}
    </div>
  );
}
